import axios from 'axios';
import AsyncStorage from '@react-native-async-storage/async-storage';
import { auth } from './firebase';

const API_BASE_URL = 'https://training-application-q6ln.onrender.com/api'; // Change to your backend URL

class AuthService {
  static async verifyToken(firebaseUser) {
    try {
      const token = await firebaseUser.getIdToken();
      const response = await axios.post(`${API_BASE_URL}/auth/verifyFirebaseToken`, {
        token
      });

      if (response.data.success) {
        return response.data.user;
      } else {
        throw new Error(response.data.message);
      }
    } catch (error) {
      console.error('Token verification error:', error);
      throw error;
    }
  }

  static async logout() {
    try {
      console.log('AuthService: Starting logout process...');
      console.log('AuthService: Firebase auth instance:', auth);

      // Check if user is currently signed in
      const currentUser = auth.currentUser;
      console.log('AuthService: Current user:', currentUser);

      if (!currentUser) {
        console.log('AuthService: No user currently signed in');
        return; // Already logged out
      }

      console.log('AuthService: Signing out user:', currentUser.email);
      await auth.signOut();
      console.log('AuthService: signOut() completed successfully');

      // Check if user is still signed in after signOut
      const userAfterSignOut = auth.currentUser;
      console.log('AuthService: User after signOut:', userAfterSignOut);

      // Force a small delay to ensure auth state change is processed
      await new Promise(resolve => setTimeout(resolve, 500));

      console.log('AuthService: Logout process completed');

    } catch (error) {
      console.error('AuthService: Logout error:', error);
      console.error('AuthService: Error details:', error.message, error.code);
      throw error;
    }
  }
}

class UserService {
  static async getProfile() {
    try {
      const token = await this.getFirebaseToken();
      const response = await axios.get(`${API_BASE_URL}/users/profile`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      return response.data.user;
    } catch (error) {
      console.error('Get profile error:', error);
      throw error;
    }
  }

  static async updateProfile(updates) {
    try {
      const token = await this.getFirebaseToken();
      const response = await axios.patch(`${API_BASE_URL}/users/profile`, updates, {
        headers: { Authorization: `Bearer ${token}` }
      });

      return response.data.user;
    } catch (error) {
      console.error('Update profile error:', error);
      throw error;
    }
  }

  static async completeOnboarding(onboardingData) {
    try {
      const token = await this.getFirebaseToken();
      const response = await axios.post(`${API_BASE_URL}/users/complete-onboarding`, onboardingData, {
        headers: { Authorization: `Bearer ${token}` }
      });

      return response.data;
    } catch (error) {
      console.error('Complete onboarding error:', error);
      throw error;
    }
  }

  static async getJobStatus(jobId) {
    try {
      const token = await this.getFirebaseToken();
      const response = await axios.get(`${API_BASE_URL}/users/job-status/${jobId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      return response.data;
    } catch (error) {
      console.error('Get job status error:', error);
      throw error;
    }
  }

  static async getLatestJobStatus() {
    try {
      const token = await this.getFirebaseToken();
      const response = await axios.get(`${API_BASE_URL}/users/latest-job-status`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      return response.data;
    } catch (error) {
      console.error('Get latest job status error:', error);
      throw error;
    }
  }

  static async getPlanVersionsAndCheckIn() {
    try {
      const token = await this.getFirebaseToken();
      const response = await axios.get(`${API_BASE_URL}/users/plan-versions-and-checkin`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      // Store in local storage for Python API usage/offline access
      try {
        await AsyncStorage.setItem('user_plan_data', JSON.stringify(response.data.data));
        console.log('Plan and Checkin data saved to local storage');
      } catch (storageError) {
        console.error('Failed to save plan data locally:', storageError);
      }

      return response.data.data;
    } catch (error) {
      console.error('Get plan versions and check-in error:', error);
      throw error;
    }
  }

  static async getStoredPlanAndCheckin() {
    try {
      const data = await AsyncStorage.getItem('user_plan_data');
      return data ? JSON.parse(data) : null;
    } catch (error) {
      console.error('Get stored plan data error:', error);
      return null;
    }
  }

  static async getAllLocalStorageData() {
    try {
      const keys = await AsyncStorage.getAllKeys();
      const stores = await AsyncStorage.multiGet(keys);
      const data = {};
      stores.forEach(([key, value]) => {
        try {
          data[key] = JSON.parse(value);
        } catch (e) {
          data[key] = value;
        }
      });
      return data;
    } catch (error) {
      console.error('Get all local storage data error:', error);
      return {};
    }
  }

  static async getFirebaseToken() {
    const user = auth.currentUser;
    if (!user) throw new Error('No authenticated user');
    return await user.getIdToken();
  }
}

class PlanService {
  static async getCurrentPlan() {
    try {
      const token = await UserService.getFirebaseToken();
      const response = await axios.get(`${API_BASE_URL}/plan/current`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      return response.data.data; // Updated to match new API response structure
    } catch (error) {
      console.error('Get current plan error:', error);
      throw error;
    }
  }

  static async getPlanHistory() {
    try {
      const token = await UserService.getFirebaseToken();
      const response = await axios.get(`${API_BASE_URL}/plan/history`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      return response.data.plans;
    } catch (error) {
      console.error('Get plan history error:', error);
      throw error;
    }
  }

  static async activatePlanVersion(planVersionId) {
    try {
      const token = await UserService.getFirebaseToken();
      const response = await axios.post(
        `${API_BASE_URL}/plan/activate-tentative`,
        { planVersionId },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      return response.data;
    } catch (error) {
      console.error('Activate plan error:', error);
      throw error;
    }
  }

  static async generateNewPlan(additionalContext = null) {
    try {
      const token = await UserService.getFirebaseToken();
      const response = await axios.post(`${API_BASE_URL}/plan/generate`, {
        additionalContext
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      return response.data;
    } catch (error) {
      console.error('Generate plan error:', error);
      throw error;
    }
  }

  static async getLatestReasonForChange() {
    try {
      const token = await UserService.getFirebaseToken();
      const response = await axios.get(`${API_BASE_URL}/plan/latest-reason-for-change`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      return response.data;
    } catch (error) {
      console.error('Get latest reason for change error:', error);
      throw error;
    }
  }
}

class CheckInService {
  static async submitCheckIn(checkInData) {
    try {
      const token = await UserService.getFirebaseToken();
      const response = await axios.post(`${API_BASE_URL}/checkin`, checkInData, {
        headers: { Authorization: `Bearer ${token}` }
      });

      return response.data;
    } catch (error) {
      console.error('Submit check-in error:', error);
      throw error;
    }
  }

  static async getCheckInHistory(page = 1) {
    try {
      const token = await UserService.getFirebaseToken();
      const response = await axios.get(`${API_BASE_URL}/checkin/history?page=${page}`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      return response.data;
    } catch (error) {
      console.error('Get check-in history error:', error);
      throw error;
    }
  }

  static async getCheckInEligibility() {
    try {
      const token = await UserService.getFirebaseToken();
      const response = await axios.get(`${API_BASE_URL}/checkin/eligibility`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      return response.data;
    } catch (error) {
      console.error('Get check-in eligibility error:', error);
      throw error;
    }
  }

  static async getLatestWeight() {
    try {
      const token = await UserService.getFirebaseToken();
      const response = await axios.get(`${API_BASE_URL}/checkin/latest-weight`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      return response.data.data;
    } catch (error) {
      console.error('Get latest weight error:', error);
      throw error;
    }
  }

  static async uploadCheckInPhotos(checkInId, photos) {
    try {
      const token = await UserService.getFirebaseToken();

      console.log('Uploading photos:', { checkInId, photosCount: photos.length });

      // Convert photos to base64 and send as JSON
      const results = [];

      for (let i = 0; i < photos.length; i++) {
        const photo = photos[i];
        console.log('Processing photo:', { index: i, uri: photo.uri, caption: photo.caption });

        try {
          // Convert image to base64
          const base64 = await this.convertImageToBase64(photo.uri);

          const response = await fetch(`${API_BASE_URL}/checkin/${checkInId}/photo-base64`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              Authorization: `Bearer ${token}`,
            },
            body: JSON.stringify({
              imageData: base64,
              caption: photo.caption || '',
              filename: `photo_${i}.jpg`
            })
          });

          if (!response.ok) {
            const errorData = await response.json();
            throw new Error(errorData.message || 'Upload failed');
          }

          const result = await response.json();
          results.push(result);
          console.log('Photo upload response:', result);
        } catch (photoError) {
          console.error(`Failed to upload photo ${i}:`, photoError);
          // Continue with other photos even if one fails
        }
      }

      return { success: true, photos: results };
    } catch (error) {
      console.error('Upload photos error:', error);
      throw error;
    }
  }

  static async convertImageToBase64(uri) {
    try {
      const response = await fetch(uri);
      const blob = await response.blob();

      return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onloadend = () => {
          const base64 = reader.result.split(',')[1]; // Remove data:image/jpeg;base64, prefix
          resolve(base64);
        };
        reader.onerror = reject;
        reader.readAsDataURL(blob);
      });
    } catch (error) {
      console.error('Error converting image to base64:', error);
      throw error;
    }
  }
}

class MacroService {
  static async logMacros(macroData) {
    try {
      const token = await UserService.getFirebaseToken();
      const response = await axios.post(`${API_BASE_URL}/macro-log`, macroData, {
        headers: { Authorization: `Bearer ${token}` }
      });

      return response.data;
    } catch (error) {
      console.error('Log macros error:', error);
      throw error;
    }
  }

  static async getTodayMacros() {
    try {
      const token = await UserService.getFirebaseToken();
      const response = await axios.get(`${API_BASE_URL}/macro-log/today`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      return response.data;
    } catch (error) {
      console.error('Get today macros error:', error);
      throw error;
    }
  }

  static async getMacroHistory(startDate, endDate) {
    try {
      const token = await UserService.getFirebaseToken();
      const response = await axios.get(`${API_BASE_URL}/macro-log`, {
        params: { startDate, endDate },
        headers: { Authorization: `Bearer ${token}` }
      });

      return response.data;
    } catch (error) {
      console.error('Get macro history error:', error);
      throw error;
    }
  }

  static async getMacroStats(days = 30) {
    try {
      const token = await UserService.getFirebaseToken();
      const response = await axios.get(`${API_BASE_URL}/macro-log/stats?days=${days}`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      return response.data.stats;
    } catch (error) {
      console.error('Get macro stats error:', error);
      throw error;
    }
  }
}

class WorkoutService {
  static async logWorkout(workoutData) {
    try {
      const token = await UserService.getFirebaseToken();
      const response = await axios.post(`${API_BASE_URL}/workout-log`, workoutData, {
        headers: { Authorization: `Bearer ${token}` }
      });

      return response.data;
    } catch (error) {
      console.error('Log workout error:', error);
      throw error;
    }
  }

  static async logWorkoutWeights(workoutWeightsData) {
    try {
      const token = await UserService.getFirebaseToken();
      const response = await axios.post(`${API_BASE_URL}/workout-log/weights`, workoutWeightsData, {
        headers: { Authorization: `Bearer ${token}` }
      });

      return response.data;
    } catch (error) {
      console.error('Log workout weights error:', error);
      throw error;
    }
  }

  static async getTodayWorkout() {
    try {
      const token = await UserService.getFirebaseToken();
      const response = await axios.get(`${API_BASE_URL}/workout-log/today`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      return response.data;
    } catch (error) {
      console.error('Get today workout error:', error);
      throw error;
    }
  }

  static async getWorkoutHistory(startDate, endDate) {
    try {
      const token = await UserService.getFirebaseToken();
      const response = await axios.get(`${API_BASE_URL}/workout-log`, {
        params: { startDate, endDate },
        headers: { Authorization: `Bearer ${token}` }
      });

      return response.data;
    } catch (error) {
      console.error('Get workout history error:', error);
      throw error;
    }
  }

  static async getWorkoutStats(days = 30) {
    try {
      const token = await UserService.getFirebaseToken();
      const response = await axios.get(`${API_BASE_URL}/workout-log/stats?days=${days}`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      return response.data.stats;
    } catch (error) {
      console.error('Get workout stats error:', error);
      throw error;
    }
  }
}

class ChatService {
  static async sendMessage(message, chatType = 'AI', additionalContext = null) {
    try {
      const token = await UserService.getFirebaseToken();
      const response = await axios.post(`${API_BASE_URL}/chat/send`, {
        message,
        chatType,
        additionalContext
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      return response.data;
    } catch (error) {
      console.error('Send message error:', error);
      throw error;
    }
  }

  static async getChatHistory(chatType = 'AI', page = 1) {
    try {
      const token = await UserService.getFirebaseToken();
      const response = await axios.get(`${API_BASE_URL}/chat/history`, {
        params: { chatType, page },
        headers: { Authorization: `Bearer ${token}` }
      });

      return response.data;
    } catch (error) {
      console.error('Get chat history error:', error);
      throw error;
    }
  }

  static async getChatSessions() {
    try {
      const token = await UserService.getFirebaseToken();
      const response = await axios.get(`${API_BASE_URL}/chat/sessions`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      return response.data;
    } catch (error) {
      console.error('Get chat sessions error:', error);
      throw error;
    }
  }
}

class FoodService {
  static async searchFoods(query) {
    try {
      const response = await axios.get(`${API_BASE_URL}/food/search`, {
        params: { query }
      });

      return response.data.foods;
    } catch (error) {
      console.error('Search foods error:', error);
      throw error;
    }
  }

  static async getFoodDetails(fdcId) {
    try {
      const response = await axios.get(`${API_BASE_URL}/food/details/${fdcId}`);

      return response.data.food;
    } catch (error) {
      console.error('Get food details error:', error);
      throw error;
    }
  }

  static async calculateMacros(food, quantity, unit = 'g') {
    try {
      const response = await axios.post(`${API_BASE_URL}/food/calculate-macros`, {
        food,
        quantity,
        unit
      });

      return response.data.macros;
    } catch (error) {
      console.error('Calculate macros error:', error);
      throw error;
    }
  }

  static async getCommonFoods() {
    try {
      const response = await axios.get(`${API_BASE_URL}/food/common`);

      return response.data.foods;
    } catch (error) {
      console.error('Get common foods error:', error);
      throw error;
    }
  }

  static async analyzeFoodText(query) {
    try {
      const response = await axios.get(`${API_BASE_URL}/food/analyze`, {
        params: { query }
      });
      return response.data.foods;
    } catch (error) {
      console.error('Analyze food text error:', error);
      throw error;
    }
  }

  static async analyzeFoodAudio(audioUri) {
    try {
      const formData = new FormData();
      formData.append('audio', {
        uri: audioUri,
        type: 'audio/m4a', // Make sure this matches recording format
        name: 'recording.m4a'
      });

      // Use fetch instead of axios because RN's FormData + Axios often fails to generate
      // the correct multipart boundary, causing the [object Object] error on the server.
      // Fetch handles this natively and correctly.
      const response = await fetch(`${API_BASE_URL}/food/analyze-audio`, {
        method: 'POST',
        headers: {
          'Accept': 'application/json',
          // DO NOT ADD 'Content-Type': 'multipart/form-data' HERE
          // The browser/network engine must generate the boundary parameter automatically.
        },
        body: formData
      });

      const result = await response.json();

      if (!response.ok) {
        throw new Error(result.message || 'Audio analysis failed');
      }

      return {
        foods: result.foods,
        transcribedText: result.transcribedText
      };
    } catch (error) {
      console.error('Analyze food audio error:', error);
      throw error;
    }
  }

}


export {
  AuthService,
  UserService,
  PlanService,
  CheckInService,
  MacroService,
  WorkoutService,
  ChatService,
  FoodService
};
